import React from 'react';

import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender } from '@tanstack/react-table';

const SortIconCustom = ({ type = '' }) => {
  return (
    <>
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9.6921 7.63806L5.72356 3.03629C5.60997 2.90457 5.39124 2.90457 5.27644 3.03629L1.3079 7.63806C1.16047 7.80965 1.2934 8.06101 1.53146 8.06101H9.46854C9.7066 8.06101 9.83953 7.80965 9.6921 7.63806Z"
          fill="black"
          fillOpacity={type === 'asc' ? '1' : '0.25'}
        />
      </svg>

      <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M9.46854 2.9375H1.53146C1.2934 2.9375 1.16047 3.18886 1.3079 3.36046L5.27644 7.96222C5.39003 8.09394 5.60876 8.09394 5.72356 7.96222L9.6921 3.36046C9.83953 3.18886 9.7066 2.9375 9.46854 2.9375Z"
          fill="black"
          fillOpacity={type === 'desc' ? '1' : '0.25'}
        />
      </svg>
    </>
  );
};

const Table = ({ columns, data, isFilter, isReport, isUserList, isSampleVideoList, isShowNoData = true }) => {
  const table = useReactTable({
    data,
    columns,
    isMultiSortEvent: () => true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="w-full">
      <table className="w-full opacity-85">
        <thead className="bg-table-header border-b border-solid border-black/6">
          {table.getHeaderGroups().map((headerGroup, index) => (
            <tr key={`table-header-${index}`}>
              {headerGroup.headers.map((header, index) => {
                return (
                  <th
                    className={`text-sm font-normal text-left py-4 ${header.column.getCanSort() && data.length === 0 ? '!pointer-events-none' : 'cursor-pointer'}`}
                    key={`table-header-sub-${index}`}
                    style={{ width: header.getSize(), maxWidth: header.getSize(), minWidth: header.getSize() }}
                    colSpan={header.colSpan}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={`flex items-center justify-between ${index < headerGroup.headers.length - 1 && 'border-r border-solid border-black/6'} px-4 text-black/85`}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <div className="pl-2">
                            <SortIconCustom type={header.column.getIsSorted()} />
                          </div>
                        )}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        {data.length > 0 ? (
          <tbody>
            {table.getRowModel().rows.map((row, index) => {
              return (
                <tr key={`table-row-${index}`} className="hover:bg-table-header">
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      <td className="px-4 py-4.25 border-b border-solid border-black/6 text-sm text-black/85" key={`table-row-sub-${index}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={columns.length}>
                <div className="w-full pt-8 pb-6 flex justify-center items-center">
                  {!isShowNoData ? (
                    <></>
                  ) : isFilter ? (
                    <img src={require('assets/resources/NoDataFilter.svg').default} alt="No data" width={824} height={116} />
                  ) : isReport ? (
                    <img src={require('assets/resources/NoDataReport.svg').default} alt="No data" width={419} height={217} />
                  ) : isUserList ? (
                    <img src={require('assets/resources/NoDataUserList.svg').default} alt="No data" width={419} height={217} />
                  ) : isSampleVideoList ? (
                    <img src={require('assets/resources/NoDataSampleVideo.svg').default} alt="No data" width={419} height={217} />
                  ) : (
                    <img src={require('assets/resources/NoData.svg').default} alt="No data" width={419} height={217} />
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
    </div>
  );
};

export default React.memo(Table);

export const IndeterminateCheckbox = ({ indeterminate, className = '', ...rest }) => {
  const ref = React.useRef(null);
  const [randomId] = React.useState(Math.random().toString(36).substring(2, 15));

  React.useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return (
    <label htmlFor={randomId} className="checkbox-button flex-middle-center">
      <input id={randomId} type="checkbox" ref={ref} className={className + 'w-4 h-4 cursor-pointer'} {...rest} />
      <span className="checkmark"></span>
    </label>
  );
};
